import { resolveLoginPath } from "@/lib/designPreview";
import { isElectronLoginRuntime } from "@/lib/electron";
import { reportHeroApiOutcome } from "@/features/heroOutage/heroOutageStore";
import { API_BASE } from "@/lib/apiBase";
import { SERVER_UPDATE_MESSAGE } from "@/features/serverUpdate/maintenanceStatus";
import {
  isServerUpdateActive,
  noteApiReachable,
  noteApiUnreachable,
} from "@/features/serverUpdate/serverUpdateStore";

export { API_BASE };

/**
 * Zentraler fetch-Wrapper für die React-Welt unter /react/.
 *
 * Verhalten:
 * - sendet Cookies immer mit (`credentials: "include"`), damit die bestehende
 *   FastAPI-Session greift (Entra-Login wie in der alten App).
 * - bei 401 oder 412 mit `reason: "reauth_required"` wird automatisch zur
 *   Login-Seite (Electron) bzw. `/auth/entra/start` (Browser) weitergeleitet.
 * - alle Fehler werden als `ApiError` geworfen, damit Aufrufer sauber `instanceof`
 *   prüfen können.
 */

function unreachableMessage(fallback: string): string {
  noteApiUnreachable();
  return isServerUpdateActive() ? SERVER_UPDATE_MESSAGE : fallback;
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

function buildUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  if (!API_BASE) return path;
  return `${API_BASE}${path.startsWith("/") ? "" : "/"}${path}`;
}

function currentNextUrl(): string {
  if (typeof window === "undefined") return "/";
  return window.location.href;
}

function isOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

function redirectToLogin(loginUrl?: string | null): never {
  // Offline ergibt ein Login-Redirect keinen Sinn: weder die lokale Login-Seite
  // noch der Microsoft-Flow sind erreichbar. Wir werfen einen Fehler, damit der
  // Aufrufer den Offline-Zustand anzeigen kann, statt in einen toten Redirect
  // (bzw. eine hängende iPad-Webview) zu laufen.
  if (isOffline()) {
    throw new ApiError(401, "offline-no-redirect");
  }
  if (isElectronLoginRuntime()) {
    const loginPath = resolveLoginPath();
    window.location.replace(loginPath);
    throw new ApiError(401, "redirecting-to-login");
  }
  const fallback = `${API_BASE || ""}/auth/entra/start`;
  const base = loginUrl || fallback;
  const sep = base.includes("?") ? "&" : "?";
  const target = `${base}${sep}next=${encodeURIComponent(currentNextUrl())}`;
  window.location.href = target;
  throw new ApiError(401, "redirecting-to-login");
}

// Standard-Timeout, damit ein hängender Request (z. B. flakiges iPad-Netz) nicht
// die ganze UI blockiert (insb. den `/me`-Auth-Gate-Spinner). Uploads/explizite
// Signale werden ausgenommen.
const DEFAULT_TIMEOUT_MS = 30_000;

interface ApiOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  json?: unknown;
  rawBody?: BodyInit | null;
  /** Timeout in ms; `0`/negativ deaktiviert das automatische Abbrechen. */
  timeoutMs?: number;
}

async function parseBody(res: Response): Promise<unknown> {
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) {
    try {
      return await res.json();
    } catch {
      return null;
    }
  }
  if (ct.startsWith("text/")) {
    try {
      return await res.text();
    } catch {
      return null;
    }
  }
  return null;
}

export interface ApiResponse<T> {
  data: T;
  headers: Headers;
}

async function performRequest<T>(
  method: string,
  path: string,
  opts: ApiOptions = {},
): Promise<ApiResponse<T>> {
  const headers = new Headers(opts.headers || {});

  let body: BodyInit | null | undefined = opts.rawBody ?? undefined;
  if (opts.json !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(opts.json);
  } else if (opts.body !== undefined && opts.rawBody === undefined) {
    body = opts.body as BodyInit;
  }

  if (
    body instanceof URLSearchParams &&
    !headers.has("Content-Type")
  ) {
    headers.set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8");
  }

  const {
    body: _ignoredBody,
    json: _ignoredJson,
    rawBody: _ignoredRaw,
    timeoutMs: optTimeoutMs,
    signal: callerSignal,
    ...restInit
  } = opts;

  // Timeout auch zusammen mit einem Aufrufer-Signal (Suche bricht ab, hängt
  // aber trotzdem nicht ewig). FormData-Uploads bleiben ohne Default-Timeout.
  const isFormUpload = typeof FormData !== "undefined" && body instanceof FormData;
  const timeoutMs = optTimeoutMs ?? (isFormUpload ? 0 : DEFAULT_TIMEOUT_MS);
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let timedOut = false;
  let signal = callerSignal ?? undefined;
  if (timeoutMs > 0 && typeof AbortController !== "undefined") {
    const timeoutController = new AbortController();
    timeoutId = setTimeout(() => {
      timedOut = true;
      timeoutController.abort();
    }, timeoutMs);
    if (callerSignal) {
      if (typeof AbortSignal !== "undefined" && typeof AbortSignal.any === "function") {
        signal = AbortSignal.any([callerSignal, timeoutController.signal]);
      } else {
        const combined = new AbortController();
        const abortCombined = () => combined.abort();
        if (callerSignal.aborted || timeoutController.signal.aborted) {
          combined.abort();
        } else {
          callerSignal.addEventListener("abort", abortCombined, { once: true });
          timeoutController.signal.addEventListener("abort", abortCombined, {
            once: true,
          });
        }
        signal = combined.signal;
      }
    } else {
      signal = timeoutController.signal;
    }
  }

  let res: Response;
  try {
    res = await fetch(buildUrl(path), {
      ...restInit,
      method,
      credentials: "include",
      headers,
      body,
      signal,
    });
  } catch (err) {
    const isAbort =
      (err instanceof DOMException || err instanceof Error) &&
      err.name === "AbortError";
    if (isAbort) {
      // Bewusst abgebrochen (z. B. neue Suchanfrage) – nicht als Netzwerkfehler melden.
      if (callerSignal?.aborted && !timedOut) {
        throw err;
      }
      reportHeroApiOutcome(path, 0);
      throw new ApiError(0, unreachableMessage("Zeitüberschreitung: Server nicht erreichbar."));
    }
    // Netzwerkfehler (offline, DNS, Verbindungsabbruch) einheitlich als ApiError.
    reportHeroApiOutcome(path, 0);
    throw new ApiError(0, unreachableMessage("Netzwerkfehler: Server nicht erreichbar."));
  } finally {
    if (timeoutId !== null) clearTimeout(timeoutId);
  }

  if (res.status === 401) {
    const parsed = (await parseBody(res)) as { login_url?: string } | null;
    redirectToLogin(parsed?.login_url);
  }

  if (res.status === 412) {
    const parsed = (await parseBody(res)) as
      | { detail?: { reason?: string; login_url?: string } | string }
      | null;
    const detail = parsed && typeof parsed.detail === "object" ? parsed.detail : null;
    if (detail && detail.reason === "reauth_required") {
      redirectToLogin(detail.login_url);
    }
  }

  const heroDegraded =
    (res.headers.get("x-ernsthaus-hero-degraded") || "").trim() === "1";

  if (res.status === 502 || res.status === 503 || res.status === 504) {
    noteApiUnreachable();
  } else {
    noteApiReachable();
  }

  if (!res.ok) {
    reportHeroApiOutcome(path, res.status, { heroDegraded });
    const parsed = await parseBody(res);
    let message = `${res.status} ${res.statusText}`;
    if (parsed && typeof parsed === "object" && "detail" in parsed) {
      const detail = (parsed as { detail?: unknown }).detail;
      if (typeof detail === "string") message = detail;
      else if (detail && typeof detail === "object") {
        const summary = (detail as { message?: unknown }).message;
        if (typeof summary === "string" && summary.trim()) message = summary.trim();
        else message = JSON.stringify(detail);
      }
    }
    if (
      (res.status === 502 || res.status === 503 || res.status === 504) &&
      isServerUpdateActive()
    ) {
      message = SERVER_UPDATE_MESSAGE;
    }
    throw new ApiError(res.status, message, parsed);
  }

  reportHeroApiOutcome(path, res.status, { heroDegraded });

  if (res.status === 204) {
    return { data: undefined as T, headers: res.headers };
  }
  const parsed = await parseBody(res);
  return { data: parsed as T, headers: res.headers };
}

async function doFetch<T>(method: string, path: string, opts: ApiOptions = {}): Promise<T> {
  const { data } = await performRequest<T>(method, path, opts);
  return data;
}

export const api = {
  getWithMeta: <T = unknown>(path: string, opts?: ApiOptions) =>
    performRequest<T>("GET", path, opts),
  get: <T = unknown>(path: string, opts?: ApiOptions) => doFetch<T>("GET", path, opts),
  post: <T = unknown>(path: string, json?: unknown, opts?: ApiOptions) =>
    doFetch<T>("POST", path, { ...opts, json }),
  put: <T = unknown>(path: string, json?: unknown, opts?: ApiOptions) =>
    doFetch<T>("PUT", path, { ...opts, json }),
  patch: <T = unknown>(path: string, json?: unknown, opts?: ApiOptions) =>
    doFetch<T>("PATCH", path, { ...opts, json }),
  del: <T = unknown>(path: string, opts?: ApiOptions) => doFetch<T>("DELETE", path, opts),
  postForm: <T = unknown>(path: string, form: FormData, opts?: ApiOptions) =>
    doFetch<T>("POST", path, { ...opts, rawBody: form }),
};
